import React, { useState, useRef } from 'react';
import { Drawer, Button, Textarea, MessagePlugin } from 'tdesign-react';
import { Form } from '../../../ui/universe';
import '../../../../style/RecognizeAgent.css';
import '../../../../style/AssistMarkType.css';

// ─── 工具函数 ─────────────────────────────────────────────────────────────────

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function stripPrefix(base64) {
  return base64.replace(/^data:.+?;base64,/, '');
}

// ─── 子组件：单题文本辅助（AnswerAssistMap）编辑弹窗 ─────────────────────────

/**
 * 产品要求：单题辅助模式只录入「题干 + 答案」两项，
 * 不提供解析、分值输入，也不支持新增多道题的辅助答案（固定一条）
 */
function AnswerAssistDialog({ visible, initialItem, onConfirm, onCancel }) {
  const [question, setQuestion] = useState(initialItem?.Question || '');
  const [answer, setAnswer] = useState(initialItem?.Answer || '');

  const handleConfirm = () => {
    if (!question.trim() && !answer.trim()) {
      MessagePlugin.warning('请至少填写题干或答案');
      return;
    }
    onConfirm({
      QuestionAuxStem: question,
      ReferenceAnswer: answer,
    });
  };

  return (
    <Drawer
      header="单题文本辅助（AnswerAssistMap）"
      visible={visible}
      onClose={onCancel}
      size="560px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="outline" onClick={onCancel}>取消</Button>
          <Button theme="primary" onClick={handleConfirm}>完成</Button>
        </div>
      }
    >
      <div className="assist-mark-dialog__body">
        <div className="assist-mark-qa-item">
          <div className="assist-mark-qa-item__fields">
            <div className="assist-mark-qa-field">
              <div className="assist-mark-qa-field__label">题干内容（Question）</div>
              <Textarea
                placeholder="例：计算 (12 + 8) × 3 = ？"
                value={question}
                autosize={{ minRows: 3, maxRows: 6 }}
                onChange={(v) => setQuestion(v)}
              />
            </div>
            <div className="assist-mark-qa-field">
              <div className="assist-mark-qa-field__label">标准答案（Answer）</div>
              <Textarea
                placeholder="例：60"
                value={answer}
                autosize={{ minRows: 3, maxRows: 6 }}
                onChange={(v) => setAnswer(v)}
              />
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
}

// ─── 子组件：标准答案图片（ImageBase64List）上传弹窗 ──────────────────────────

function ImageListDialog({ visible, initialImages, onConfirm, onCancel, maxCount }) {
  // images: [{id, previewUrl, base64}]
  const [images, setImages] = useState(() => initialImages || []);
  const fileInputRef = useRef(null);

  // maxCount 存在时限制张数（整页辅助批改 Type=2/3：后端校验最多 1 张）
  const limitReached = !!maxCount && images.length >= maxCount;

  const handleFiles = async (files) => {
    const ACCEPT = ['jpg', 'jpeg', 'png', 'bmp', 'pdf'];
    const MAX_MB = 10;
    const remain = maxCount ? maxCount - images.length : Infinity;
    if (remain <= 0) {
      MessagePlugin.warning(`答案图最多上传 ${maxCount} 张`);
      return;
    }

    const accepted = [];
    for (const file of files) {
      if (accepted.length >= remain) {
        MessagePlugin.warning(`答案图最多上传 ${maxCount} 张，多出的已忽略`);
        break;
      }
      const ext = file.name.split('.').pop().toLowerCase();
      if (!ACCEPT.includes(ext)) {
        MessagePlugin.warning(`不支持 ${ext} 格式，请上传 ${ACCEPT.join('/')} 图片`);
        continue;
      }
      const raw = await fileToBase64(file);
      if (raw.length > MAX_MB * 1024 * 1024) {
        MessagePlugin.warning(`${file.name} 超过 ${MAX_MB}M 限制`);
        continue;
      }
      accepted.push({
        id: `${Date.now()}-${Math.random()}`,
        previewUrl: raw,
        base64: stripPrefix(raw),
      });
    }
    if (accepted.length > 0) {
      setImages((prev) => [...prev, ...accepted]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleConfirm = () => {
    if (images.length === 0) {
      MessagePlugin.warning('请至少上传一张答案图片');
      return;
    }
    // 兜底：历史数据可能超过上限，提交前裁剪
    onConfirm(images.slice(0, maxCount || images.length).map((img) => img.base64));
  };

  return (
    <Drawer
      header={
        maxCount
          ? `标准答案图片（最多 ${maxCount} 张）`
          : '标准答案图片（ImageBase64List.N）'
      }
      visible={visible}
      onClose={onCancel}
      size="560px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="outline" onClick={onCancel}>取消</Button>
          <Button theme="primary" onClick={handleConfirm}>完成</Button>
        </div>
      }
    >
      <div className="assist-mark-dialog__body">
        {/* 上传区 */}
        <div
          className={`assist-mark-img-dropzone ${limitReached ? 'is-disabled' : ''}`}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => {
            if (limitReached) {
              MessagePlugin.warning(`答案图最多上传 ${maxCount} 张`);
              return;
            }
            fileInputRef.current?.click();
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.bmp,.pdf"
            style={{ display: 'none' }}
            onChange={(e) => {
              handleFiles(Array.from(e.target.files));
              e.target.value = '';
            }}
          />
          <span className="assist-mark-img-dropzone__icon" />
          <div className="assist-mark-img-dropzone__title">
            {maxCount
              ? `上传标准答案整页图（最多 ${maxCount} 张）`
              : '上传答案图片或答案 PDF（可多页多张）'}
          </div>
          <div className="assist-mark-img-dropzone__desc">
            {maxCount
              ? `整页辅助批改：答案图仅支持 ${maxCount} 张，按 List 顺序对应`
              : '非答题卡场景：请上传标准答案整页图，多张按 List 顺序对应'}
          </div>
        </div>

        {/* 已上传列表 */}
        <div className="assist-mark-img-section-title">
          <span>已上传答案图片</span>
          <span className="assist-mark-img-section-count">
            {images.length > 0
              ? maxCount
                ? `${images.length} / ${maxCount} 张`
                : `${images.length} 张`
              : '尚未上传'}
          </span>
        </div>
        {images.length === 0 ? (
          <div className="assist-mark-img-empty">
            <span className="assist-mark-img-empty__icon" />
            <span>暂无答案图片</span>
          </div>
        ) : (
          <div className="assist-mark-img-grid">
            {images.map((img, idx) => (
              <div key={img.id} className="assist-mark-img-cell">
                <img src={img.previewUrl} alt={`答案${idx + 1}`} className="assist-mark-img-cell__img" />
                <div className="assist-mark-img-cell__label">第 {idx + 1} 张</div>
                <span
                  className="assist-mark-img-cell__remove"
                  onClick={() => setImages((prev) => prev.filter((_, i) => i !== idx))}
                >
                  ×
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
}

//─── 主组件 ──────────────────────────────────────────────────────────────────

// ─── 辅助：解析 AnswerAssistMap JSON 字符串为内部数组 ─────────────────────────

function parseAnswerAssistMap(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    // API 示例是单个对象，也兼容数组
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
}

// ─── 主组件 ──────────────────────────────────────────────────────────────────

function AssistMarkTypeRenderer({ params, value, onChange, allParamsValue, onParamsChange }) {
  const [qaDialogOpen, setQaDialogOpen] = useState(false);
  const [imgDialogOpen, setImgDialogOpen] = useState(false);

  const options = params.options || [];

  // 整页辅助批改（Type=2/3）的答案图有张数上限：
  // 后端校验「AssistMarkType=2 时 ImageBase64List 最多 1 张」。
  // 菜单配置可在 option 上用 maxCount 覆盖（不配则 2/3 默认 1 张，其它类型不限）
  const currentOption = options.find((o) => o.value === value);
  const answerMaxCount =
    currentOption?.maxCount ?? (value === 2 || value === 3 ? 1 : undefined);

  // AnswerAssistMap 在 paramsValue 中以 JSON 字符串存储，ImageBase64List 以数组存储
  const answerAssistMapRaw = allParamsValue?.AnswerAssistMap || null;
  const imageBase64List = allParamsValue?.ImageBase64List || null;

  // 解析出内部数组（用于 UI 显示计数、初始化抽屉表单）
  const answerAssistItems = parseAnswerAssistMap(answerAssistMapRaw);
  const qaCount = answerAssistItems.length;
  const imgCount = Array.isArray(imageBase64List) ? imageBase64List.length : 0;

  // 切换批改类型时清除另一种辅助数据
  // 注意：类型切换和清空必须合并成「一次」onParamsChange 提交。
  // 若拆成 onParamsChange(清空) + onChange(设类型) 两次调用，二者共享同一份
  // stale 的 paramsValue 闭包，后一次会把前一次清掉的 AnswerAssistMap/ImageBase64List
  // 重新展开写回，导致切换类型后旧答案仍被带入请求。
  const handleTypeChange = (newType) => {
    onParamsChange &&
      onParamsChange({
        [params.key]: newType,
        AnswerAssistMap: null,
        ImageBase64List: null,
      });
  };

  // type=1 配置确认：单题固定为一条，直接序列化成对象
  const handleQAConfirm = (item) => {
    onParamsChange && onParamsChange({ AnswerAssistMap: JSON.stringify(item) });
    setQaDialogOpen(false);
  };

  // type=2/3 图片确认（保持数组，后端接收 ImageBase64List.N 列表）
  const handleImgConfirm = (base64List) => {
    const list = answerMaxCount
      ? base64List.slice(0, answerMaxCount)
      : base64List;
    onParamsChange && onParamsChange({ ImageBase64List: list });
    setImgDialogOpen(false);
  };

  return (
    <>
      <Form.Item className="column" label={params.label}>
        {/* 四宫格卡片 */}
        <div className="recognize-agent-task-type-list">
          {options.map((option) => (
            <div
              key={option.value}
              className={`recognize-agent-task-type-item ${value === option.value ? 'active' : ''}`}
              onClick={() => handleTypeChange(option.value)}
            >
              <span className="recognize-agent-task-type-item__title">{option.text}</span>
              {option.des && (
                <span className="recognize-agent-task-type-item__des">{option.des}</span>
              )}
            </div>
          ))}
        </div>

        {/* type=1：单题文本辅助入口 */}
        {value === 1 && (
          <div
            className={`assist-mark-entry ${qaCount > 0 ? 'assist-mark-entry--filled' : ''}`}
            onClick={() => setQaDialogOpen(true)}
          >
            <span className="assist-mark-entry__icon assist-mark-entry__icon--qa" />
            <span className="assist-mark-entry__title">单题文本辅助</span>
            {qaCount > 0
              ? <span className="assist-mark-entry__count">已录入题干与答案</span>
              : <span className="assist-mark-entry__placeholder">点击配置题干和答案</span>
            }
            <span className="assist-mark-entry__action">配置 &gt;</span>
          </div>
        )}

        {/* type=2/3：标准答案图片入口 */}
        {(value === 2 || value === 3) && (
          <div
            className={`assist-mark-entry ${imgCount > 0 ? 'assist-mark-entry--filled' : ''}`}
            onClick={() => setImgDialogOpen(true)}
          >
            <span className="assist-mark-entry__icon assist-mark-entry__icon--img" />
            <span className="assist-mark-entry__title">标准答案图片</span>
            {imgCount > 0
              ? (
                <span className="assist-mark-entry__count">
                  已上传 {imgCount}
                  {answerMaxCount ? ` / ${answerMaxCount}` : ''} 张
                </span>
              )
              : (
                <span className="assist-mark-entry__placeholder">
                  {answerMaxCount
                    ? `点击上传答案图片（最多 ${answerMaxCount} 张）`
                    : '点击上传答案图片'}
                </span>
              )
            }
            <span className="assist-mark-entry__action">配置 &gt;</span>
          </div>
        )}
      </Form.Item>

      {/* 单题文本辅助抽屉 */}
      {qaDialogOpen && (
        <AnswerAssistDialog
          visible={qaDialogOpen}
          initialItem={
            answerAssistItems.length > 0
              ? {
                  Question: answerAssistItems[0].QuestionAuxStem || '',
                  Answer: answerAssistItems[0].ReferenceAnswer || '',
                }
              : null
          }
          onConfirm={handleQAConfirm}
          onCancel={() => setQaDialogOpen(false)}
        />
      )}

      {/* 标准答案图片抽屉 */}
      {imgDialogOpen && (
        <ImageListDialog
          maxCount={answerMaxCount}
          visible={imgDialogOpen}
          initialImages={Array.isArray(imageBase64List)
            ? imageBase64List.map((b64, i) => ({
                id: i,
                previewUrl: `data:image/jpeg;base64,${b64}`,
                base64: b64,
              }))
            : []
          }
          onConfirm={handleImgConfirm}
          onCancel={() => setImgDialogOpen(false)}
        />
      )}
    </>
  );
}

export default AssistMarkTypeRenderer;
